import React from 'react';
import type { ReportView, ZReportLayoutConfig } from 'shared-models/browser';
import { formatMoney, isolateAmountLtr, roundMoney } from '../../../../utils/money';
import {
  getDefaultLabelForKey,
  mergeZReportLayoutConfig,
} from './z-report-layout.catalog';
import classes from './pos-report-view.module.scss';

const REPORT_TITLES: Record<ReportView['kind'], string> = {
  x: 'דוח X',
  z: 'דוח Z',
  worker: 'דוח עובד',
};

const MONEY_ROW_KEYS = new Set([
  'row:salesWithOpenOrders',
  'row:openOrdersTotal',
  'row:closedRevenue',
  'row:vatAmount',
  'row:canceledItemsAmount',
  'row:discountsAmount',
  'row:avgPerDiner',
  'row:avgPerDinerTakeAway',
  'row:tipsTotalMain',
  'row:salesByReportGroupFood',
  'row:salesByReportGroupBeverages',
  'row:salesByReportGroupOther',
  'row:tipsCredit',
  'row:tipsCash',
  'row:tipsTotalSection',
  'row:tipsStationary',
  'row:receiptsCash',
  'row:receiptsCredit',
  'row:receiptsTotal',
  'row:paymentsCredit',
  'row:paymentsCash',
  'row:paymentsTotal',
  'row:creditCharges',
  'row:creditRefunds',
  'row:creditTotal',
  'row:cashCharges',
  'row:cashRefunds',
  'row:cashTotal',
  'row:drawerCashTotalWithTips',
  'row:drawerTipsTotal',
  'row:openingCash',
  'row:countedCash',
  'row:cashMinusCreditTips',
  'row:expectedCash',
]);

const pad = (n: number) => String(n).padStart(2, '0');
const formatDateTime = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean; dataTestId?: string }> = ({ label, value, bold, dataTestId }) => (
  <div className={bold ? `${classes.row} ${classes.rowBold}` : classes.row} data-testid={dataTestId}>
    <span className={classes.rowLabel}>{label}</span>
    <span className={classes.rowValue}>{value}</span>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={classes.sectionTitle}>{children}</div>
);

type RenderRow = {
  key: string;
  label: string;
  value: React.ReactNode;
  bold?: boolean;
  dataTestId?: string;
};

type RenderSection = {
  key: string;
  title?: string;
  rows: RenderRow[];
  extra?: React.ReactNode;
};

export type PosReportViewProps = {
  report: ReportView;
  innerRef?: React.Ref<HTMLDivElement>;
  variant?: 'screen' | 'print';
  layoutConfigOverride?: ZReportLayoutConfig | null;
};

function renderModularReport(
  report: ReportView,
  variant: 'screen' | 'print',
  config: ZReportLayoutConfig,
): React.ReactNode {
  const drawerCashWithTips = report.drawers.reduce((sum, drawer) => sum + drawer.cashTotalWithTips, 0);
  const entryByKey = new Map(config.entries.map((entry) => [entry.key, entry]));

  const isVisible = (key: string): boolean => entryByKey.get(key)?.visible ?? true;
  const labelFor = (key: string, fallback: string): string => {
    const candidate = entryByKey.get(key)?.label?.trim();
    if (!candidate) {
      return fallback;
    }
    // Preserve dynamic VAT label by default; only use custom text when user changed it from the catalog default.
    if (key === 'row:vatAmount') {
      const defaultVatLabel = getDefaultLabelForKey(key).trim();
      if (candidate === defaultVatLabel) {
        return fallback;
      }
    }
    return candidate;
  };
  const boldFor = (key: string, fallback = false): boolean => entryByKey.get(key)?.bold ?? fallback;
  const negativeFor = (key: string, fallback = false): boolean => entryByKey.get(key)?.negative ?? fallback;
  const isDrawerDetailRowKey = (key: string): boolean =>
    key === 'row:drawerHeader' || key === 'row:drawerCashTotalWithTips' || key === 'row:drawerTipsTotal';

  const stationaryRows =
    report.kind !== 'worker' && isVisible('row:tipsStationary')
      ? (report.stationaryTipsByStation ?? [])
        .filter((item) => item.stationaryName.trim().length > 0 && Math.abs(item.tipsTotal) > 0)
      : [];

  const amountByRowKey = new Map<string, number>([
    ['row:salesWithOpenOrders', report.salesWithOpenOrders],
    ['row:openOrdersTotal', report.openOrdersTotal],
    ['row:closedRevenue', report.closedRevenue],
    ['row:vatAmount', report.vatAmount],
    ['row:canceledItemsAmount', report.canceledItemsAmount],
    ['row:discountsAmount', report.discountsAmount],
    ['row:avgPerDiner', report.avgPerDiner],
    ['row:avgPerDinerTakeAway', report.avgPerDinerTakeAway],
    ['row:tipsTotalMain', report.tipsTotal],
    ['row:salesByReportGroupFood', report.salesByReportGroup.food],
    ['row:salesByReportGroupBeverages', report.salesByReportGroup.beverages],
    ['row:salesByReportGroupOther', report.salesByReportGroup.other],
    ['row:tipsCredit', report.tipsCredit],
    ['row:tipsCash', report.tipsCash],
    ['row:tipsTotalSection', report.tipsTotal],
    ['row:receiptsCash', report.receiptsCash],
    ['row:receiptsCredit', report.receiptsCredit],
    ['row:receiptsTotal', report.receiptsTotal],
    ['row:paymentsCredit', report.paymentsCredit],
    ['row:paymentsCash', report.paymentsCash],
    ['row:paymentsTotal', report.paymentsTotal],
    ['row:creditCharges', report.creditCharges],
    ['row:creditRefunds', report.creditRefunds],
    ['row:creditTotal', report.creditTotal],
    ['row:cashCharges', report.cashCharges],
    ['row:cashRefunds', report.cashRefunds],
    ['row:cashTotal', report.cashTotal],
    ['row:openingCash', report.openingCash],
    ['row:countedCash', report.closingCash ?? 0],
    ['row:cashMinusCreditTips', roundMoney(report.paymentsCash - report.tipsCredit)],
    ['row:expectedCash', roundMoney(report.openingCash + drawerCashWithTips)],
  ]);

  const resolveValueForInstance = (
    instanceKey: string,
    sourceKey: string,
    fallback: React.ReactNode,
  ): React.ReactNode => {
    if (!negativeFor(instanceKey) || !MONEY_ROW_KEYS.has(sourceKey)) {
      return fallback;
    }
    const amount = amountByRowKey.get(sourceKey);
    if (typeof amount !== 'number') {
      return fallback;
    }
    return <Money value={-Math.abs(amount)} />;
  };

  const sections: RenderSection[] = [
    {
      key: 'section:salesSummary',
      title: 'סיכום מכירות',
      rows: [
        { key: 'row:salesWithOpenOrders', label: 'סה"כ מכירות עם הזמנות פתוחות (לא כולל טיפים)', value: <Money value={report.salesWithOpenOrders} /> },
        { key: 'row:openOrdersTotal', label: 'סכום כולל הזמנות פתוחות (לא כולל טיפים)', value: <Money value={report.openOrdersTotal} /> },
        { key: 'row:openOrdersCount', label: 'כמות הזמנות פתוחות', value: report.openOrdersCount },
        { key: 'row:closedRevenue', label: 'סה"כ הכנסות - הזמנות סגורות (לא כולל טיפים)', value: <Money value={report.closedRevenue} /> },
        { key: 'row:vatAmount', label: `מע"מ ${report.vatRatePercent}%`, value: <Money value={report.vatAmount} /> },
        { key: 'row:dinersCount', label: 'סה"כ סועדים', value: report.dinersCount },
        { key: 'row:canceledItemsCount', label: 'ביטול פריטים (כמות)', value: report.canceledItemsCount },
        { key: 'row:canceledItemsAmount', label: 'ביטול פריטים (סכום)', value: <Money value={report.canceledItemsAmount} /> },
        { key: 'row:discountsAmount', label: 'הנחות (סכום)', value: <Money value={report.discountsAmount} /> },
        { key: 'row:canceledOrdersCount', label: 'הזמנות מבוטלות (כמות)', value: report.canceledOrdersCount },
        { key: 'row:avgPerDiner', label: 'ממוצע לסועד (לא כולל טיפים)', value: <Money value={report.avgPerDiner} /> },
        { key: 'row:avgPerDinerTakeAway', label: 'ממוצע לסועד - מהירה', value: <Money value={report.avgPerDinerTakeAway} /> },
        { key: 'row:tipsTotalMain', label: 'סה"כ טיפים (סכום טיפים כולל)', value: <Money value={report.tipsTotal} /> },
      ],
    },
    {
      key: 'section:salesByCategory',
      title: 'פירוט הכנסות לפי קטגוריה',
      rows: [
        { key: 'row:salesByReportGroupFood', label: 'אוכל', value: <Money value={report.salesByReportGroup.food} /> },
        { key: 'row:salesByReportGroupBeverages', label: 'משקאות', value: <Money value={report.salesByReportGroup.beverages} /> },
        { key: 'row:salesByReportGroupOther', label: 'אחר', value: <Money value={report.salesByReportGroup.other} /> },
      ],
    },
    {
      key: 'section:tipsByPayment',
      title: 'טיפים לפי סוג תשלום',
      rows: [
        { key: 'row:tipsCredit', label: 'טיפים אשראי', value: <Money value={report.tipsCredit} /> },
        { key: 'row:tipsCash', label: 'טיפים מזומן', value: <Money value={report.tipsCash} /> },
        {
          key: 'row:tipsTotalSection',
          label: 'סה"כ טיפים',
          value: <Money value={report.tipsTotal} />,
          bold: true,
          dataTestId: variant === 'screen' ? 'report-tips-total' : undefined,
        },
      ],
      extra: (
        <>
          {stationaryRows.map((station) => (
            <Row
              key={`row:tipsStationary:${station.stationaryName}`}
              label={`${labelFor('row:tipsStationary', 'טיפים עמדה')} ${station.stationaryName}`}
              value={negativeFor('row:tipsStationary') ? <Money value={-Math.abs(station.tipsTotal)} /> : <Money value={station.tipsTotal} />}
              bold={boldFor('row:tipsStationary')}
            />
          ))}
        </>
      ),
    },
    {
      key: 'section:receiptsSummary',
      title: 'סיכום סוגי תקבולים',
      rows: [
        { key: 'row:receiptsCash', label: 'תקבול מזומן (כולל טיפים)', value: <Money value={report.receiptsCash} /> },
        { key: 'row:receiptsCredit', label: 'תקבול אשראי (כולל טיפים)', value: <Money value={report.receiptsCredit} /> },
        { key: 'row:receiptsTotal', label: 'סה"כ תקבולים', value: <Money value={report.receiptsTotal} />, bold: true },
      ],
    },
    {
      key: 'section:paymentsSummary',
      title: 'סיכום סוגי תשלומים',
      rows: [
        { key: 'row:paymentsCredit', label: 'אשראי (לא כולל טיפים)', value: <Money value={report.paymentsCredit} /> },
        { key: 'row:paymentsCash', label: 'מזומן (לא כולל טיפים)', value: <Money value={report.paymentsCash} /> },
        { key: 'row:paymentsTotal', label: 'סה"כ סוגי תשלום', value: <Money value={report.paymentsTotal} />, bold: true },
      ],
    },
    {
      key: 'section:paymentDetails',
      title: 'פירוט סוגי תשלומים',
      rows: [
        { key: 'row:creditCharges', label: 'אשראי - סה"כ חיובים (לא כולל טיפים)', value: <Money value={report.creditCharges} /> },
        { key: 'row:creditRefunds', label: 'אשראי - סה"כ זיכויים', value: <Money value={report.creditRefunds} /> },
        { key: 'row:creditTotal', label: 'אשראי - סה"כ אשראי', value: <Money value={report.creditTotal} />, bold: true },
        { key: 'row:cashCharges', label: 'מזומן - סה"כ חיובים (לא כולל טיפים)', value: <Money value={report.cashCharges} /> },
        { key: 'row:cashRefunds', label: 'מזומן - סה"כ זיכויים', value: <Money value={report.cashRefunds} /> },
        { key: 'row:cashTotal', label: 'מזומן - סה"כ מזומן', value: <Money value={report.cashTotal} />, bold: true },
      ],
    },
    {
      key: 'section:invoices',
      title: 'חשבוניות שהופקו',
      rows: [
        { key: 'row:chargeTransactionsCount', label: 'עסקאות חיוב', value: report.chargeTransactionsCount },
        { key: 'row:refundTransactionsCount', label: 'עסקאות זיכוי', value: report.refundTransactionsCount },
      ],
    },
    {
      key: 'section:drawers',
      title: 'פירוט מגירות מזומן',
      rows: [
        { key: 'row:drawerHeader', label: 'כותרת מגירה (שם מדפסת)', value: '' },
        { key: 'row:drawerCashTotalWithTips', label: 'סה"כ מזומן (כולל טיפים)', value: '' },
        { key: 'row:drawerTipsTotal', label: 'מתוכם סה"כ טיפים', value: '' },
        { key: 'row:openingCash', label: 'מזומן פתיחה', value: <Money value={report.openingCash} />, bold: true },
        { key: 'row:countedCash', label: 'מזומן נספר בקופה', value: <Money value={report.closingCash ?? 0} />, bold: true },
        { key: 'row:cashMinusCreditTips', label: 'סה"כ מזומן בהפחתת טיפים אשראי ומזומן', value: <Money value={roundMoney(report.paymentsCash - report.tipsCredit)} />, bold: true },
        { key: 'row:expectedCash', label: 'מזומן צפוי בקופה', value: <Money value={roundMoney(report.openingCash + drawerCashWithTips)} />, bold: true },
      ],
      extra: (
        report.drawers.length === 0 && report.openingCash === 0 ? (
          <div className={classes.emptyRow}>אין תנועות מזומן</div>
        ) : (
          <>
            {report.drawers.map((drawer) => (
              <React.Fragment key={drawer.printerId ?? 'none'}>
                {isVisible('row:drawerHeader') && (
                  <div className={classes.subHeader}>{drawer.printerName}</div>
                )}
                {isVisible('row:drawerCashTotalWithTips') && (
                  <Row
                    label={labelFor('row:drawerCashTotalWithTips', 'סה"כ מזומן (כולל טיפים)')}
                    value={negativeFor('row:drawerCashTotalWithTips') ? <Money value={-Math.abs(drawer.cashTotalWithTips)} /> : <Money value={drawer.cashTotalWithTips} />}
                  />
                )}
                {isVisible('row:drawerTipsTotal') && (
                  <Row
                    label={labelFor('row:drawerTipsTotal', 'מתוכם סה"כ טיפים')}
                    value={negativeFor('row:drawerTipsTotal') ? <Money value={-Math.abs(drawer.tipsTotal)} /> : <Money value={drawer.tipsTotal} />}
                  />
                )}
              </React.Fragment>
            ))}
          </>
        )
      ),
    },
  ];

  const sectionMap = new Map<string, RenderSection>(sections.map((section) => [section.key, section]));
  const rowMap = new Map<string, RenderRow>(sections.flatMap((section) => section.rows.map((row) => [row.key, row] as const)));

  const sectionLayout: Array<{ sectionKey: string; rows: Array<{ instanceKey: string; sourceKey: string }> }> = [];
  const leadingRows: Array<{ instanceKey: string; sourceKey: string }> = [];
  let currentSection: { sectionKey: string; rows: Array<{ instanceKey: string; sourceKey: string }> } | null = null;

  config.entries.forEach((entry) => {
    if (entry.type === 'section' && sectionMap.has(entry.key)) {
      currentSection = { sectionKey: entry.key, rows: [] };
      sectionLayout.push(currentSection);
      return;
    }
    if (entry.type !== 'row') {
      return;
    }
    const sourceKey = entry.sourceKey ?? entry.key;
    if (!rowMap.has(sourceKey)) {
      return;
    }
    const rowRef = { instanceKey: entry.key, sourceKey };
    if (!currentSection) {
      leadingRows.push(rowRef);
      return;
    }
    currentSection.rows.push(rowRef);
  });

  if (leadingRows.length > 0 && sectionLayout.length > 0) {
    sectionLayout[0].rows = [...leadingRows, ...sectionLayout[0].rows];
  }

  return (
    <>
      {sectionLayout
        .filter((layout) => isVisible(layout.sectionKey) && sectionMap.has(layout.sectionKey))
        .map((layout) => {
        const section = sectionMap.get(layout.sectionKey);
        if (!section) {
          return null;
        }
        const defaultSectionLabel = section.title || getDefaultLabelForKey(section.key);
        const sectionTitle = labelFor(section.key, defaultSectionLabel);
        const rows = layout.rows
          .map((rowRef) => {
            const template = rowMap.get(rowRef.sourceKey);
            if (!template) {
              return null;
            }
            return { ...template, instanceKey: rowRef.instanceKey, sourceKey: rowRef.sourceKey };
          })
          .filter((row): row is RenderRow & { instanceKey: string; sourceKey: string } => row !== null)
          .filter((row) => {
            if (isDrawerDetailRowKey(row.sourceKey)) {
              return false;
            }
            if (row.sourceKey === 'row:salesWithOpenOrders' || row.sourceKey === 'row:openOrdersTotal' || row.sourceKey === 'row:openOrdersCount') {
              if (report.kind !== 'x') {
                return false;
              }
            }
            if (row.sourceKey === 'row:countedCash') {
              if (!report.shiftCloseCashBalancesEnabled || report.closingCash == null) {
                return false;
              }
            }
            return isVisible(row.instanceKey);
          });

        return (
          <React.Fragment key={section.key}>
            <SectionTitle>{sectionTitle}</SectionTitle>
            <div className={classes.section}>
              {rows.map((row) => (
                <Row
                  key={row.instanceKey}
                  label={labelFor(row.instanceKey, row.label)}
                  value={resolveValueForInstance(row.instanceKey, row.sourceKey, row.value)}
                  bold={boldFor(row.instanceKey, row.bold ?? false)}
                  dataTestId={row.dataTestId}
                />
              ))}
              {section.extra}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}

// Summary: Presentational X/Z/worker report — renders a ReportView per the locked layout (RTL).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
export const PosReportView: React.FC<PosReportViewProps> = ({ report, innerRef, variant = 'screen', layoutConfigOverride }) => {
  const r = report;
  const printableZCloseTime =
    r.kind === 'z' && variant === 'print'
      ? (r.closeTime ?? new Date().toISOString())
      : r.closeTime;
  // Open-orders figures only apply to the live X snapshot; Z/worker sessions are already closed.
  const showOpenOrders = r.kind === 'x';
  // Expected cash is the opening float plus every drawer's collected cash, including cash tips.
  const drawerCashWithTips = r.drawers.reduce((sum, d) => sum + d.cashTotalWithTips, 0);

  const reportLayout = mergeZReportLayoutConfig(layoutConfigOverride ?? r.zReportLayoutConfig ?? null);
  const modularLayout = r.kind === 'z' || r.kind === 'x' ? reportLayout : null;
  const rowBold = (key: string, fallback = false): boolean => {
    const entry = reportLayout.entries.find((item) => item.key === key && item.type === 'row');
    return entry?.bold ?? fallback;
  };

  return (
    <div ref={innerRef} className={variant === 'print' ? `${classes.paper} ${classes.paperPrint}` : classes.paper}>
      <div className={classes.header}>
        {r.header.split('\n').map((line, index) => (
          <span key={index} className={classes.businessLine}>{line}</span>
        ))}
      </div>

      <div className={classes.metaBlock}>
        <div className={classes.metaRow}>מספר משמרת: {r.sessionName ?? '--'}</div>
        <div className={classes.metaRow}>זמן פתיחת משמרת: {formatDateTime(r.openTime)}</div>
        {r.kind === 'z' && <div className={classes.metaRow}>זמן סגירת משמרת: {formatDateTime(printableZCloseTime)}</div>}
        {r.kind !== 'z' && <div className={classes.metaRow}>זמן הפקה: {formatDateTime(r.printTime)}</div>}
        {r.workerName && <div className={classes.metaRow}>עובד: {r.workerName}</div>}
        <div className={classes.metaRow}>משתמש: {r.openerName ?? '--'}</div>
      </div>

      <div className={classes.title}>{REPORT_TITLES[r.kind]}</div>

      {modularLayout ? (
        renderModularReport(r, variant, modularLayout)
      ) : (
        <>
          <div className={classes.section}>
            {showOpenOrders && (
              <Row label='סה"כ מכירות עם הזמנות פתוחות (לא כולל טיפים)' value={<Money value={r.salesWithOpenOrders} />} />
            )}
            <Row label='סה"כ הכנסות - הזמנות סגורות (לא כולל טיפים)' value={<Money value={r.closedRevenue} />} />
            {showOpenOrders && (
              <Row label="סכום כולל הזמנות פתוחות (לא כולל טיפים)" value={<Money value={r.openOrdersTotal} />} />
            )}
            {showOpenOrders && <Row label="כמות הזמנות פתוחות" value={r.openOrdersCount} />}
            <Row label={`מע"מ ${r.vatRatePercent}%`} value={<Money value={r.vatAmount} />} />
            <Row label='סה"כ סועדים' value={r.dinersCount} />
            <Row label="ביטול פריטים (כמות)" value={r.canceledItemsCount} />
            <Row label="ביטול פריטים (סכום)" value={<Money value={r.canceledItemsAmount} />} />
            <Row label="הנחות (סכום)" value={<Money value={r.discountsAmount} />} />
            <Row label="הזמנות מבוטלות (כמות)" value={r.canceledOrdersCount} />
            <Row label="ממוצע לסועד (לא כולל טיפים)" value={<Money value={r.avgPerDiner} />} />
            <Row label="ממוצע לסועד - מהירה" value={<Money value={r.avgPerDinerTakeAway} />} />
            <Row label='סה"כ טיפים (סכום טיפים כולל)' value={<Money value={r.tipsTotal} />} bold={rowBold('row:tipsTotalMain')} />
          </div>

          <SectionTitle>פירוט הכנסות לפי קטגוריה</SectionTitle>
          <div className={classes.section}>
            <Row label="אוכל" value={<Money value={r.salesByReportGroup.food} />} />
            <Row label="משקאות" value={<Money value={r.salesByReportGroup.beverages} />} />
            <Row label="אחר" value={<Money value={r.salesByReportGroup.other} />} />
          </div>

          <SectionTitle>טיפים לפי סוג תשלום</SectionTitle>
          <div className={classes.section}>
            <Row label="טיפים אשראי" value={<Money value={r.tipsCredit} />} />
            <Row label="טיפים מזומן" value={<Money value={r.tipsCash} />} />
            <Row
              label='סה"כ טיפים'
              value={<Money value={r.tipsTotal} />}
              bold={rowBold('row:tipsTotalSection', true)}
              dataTestId={variant === 'screen' ? 'report-tips-total' : undefined}
            />
          </div>

          <SectionTitle>סיכום סוגי תקבולים</SectionTitle>
          <div className={classes.section}>
            <Row label="תקבול מזומן (כולל טיפים)" value={<Money value={r.receiptsCash} />} />
            <Row label="תקבול אשראי (כולל טיפים)" value={<Money value={r.receiptsCredit} />} />
            <Row label='סה"כ תקבולים' value={<Money value={r.receiptsTotal} />} bold={rowBold('row:receiptsTotal', true)} />
          </div>

          <SectionTitle>סיכום סוגי תשלומים</SectionTitle>
          <div className={classes.section}>
            <Row label="אשראי (לא כולל טיפים)" value={<Money value={r.paymentsCredit} />} />
            <Row label="מזומן (לא כולל טיפים)" value={<Money value={r.paymentsCash} />} />
            <Row label='סה"כ סוגי תשלום' value={<Money value={r.paymentsTotal} />} bold={rowBold('row:paymentsTotal', true)} />
          </div>

          <SectionTitle>פירוט סוגי תשלומים</SectionTitle>
          <div className={classes.section}>
            <div className={classes.subHeader}>אשראי</div>
            <Row label='סה"כ חיובים (לא כולל טיפים)' value={<Money value={r.creditCharges} />} />
            <Row label='סה"כ זיכויים' value={<Money value={r.creditRefunds} />} />
            <Row label='סה"כ אשראי' value={<Money value={r.creditTotal} />} bold={rowBold('row:creditTotal', true)} />
            <div className={classes.subHeader}>מזומן</div>
            <Row label='סה"כ חיובים (לא כולל טיפים)' value={<Money value={r.cashCharges} />} />
            <Row label='סה"כ זיכויים' value={<Money value={r.cashRefunds} />} />
            <Row label='סה"כ מזומן' value={<Money value={r.cashTotal} />} bold={rowBold('row:cashTotal', true)} />
          </div>

          <SectionTitle>חשבוניות שהופקו</SectionTitle>
          <div className={classes.section}>
            <Row label="עסקאות חיוב" value={r.chargeTransactionsCount} />
            <Row label="עסקאות זיכוי" value={r.refundTransactionsCount} />
          </div>

          <SectionTitle>פירוט מגירות מזומן</SectionTitle>
          <div className={classes.section}>
            {r.drawers.length === 0 && r.openingCash === 0 ? (
              <div className={classes.emptyRow}>אין תנועות מזומן</div>
            ) : (
              <>
                {r.drawers.map((drawer) => (
                  <React.Fragment key={drawer.printerId ?? 'none'}>
                    <div className={classes.subHeader}>{drawer.printerName}</div>
                    <Row label='סה"כ מזומן (כולל טיפים)' value={<Money value={drawer.cashTotalWithTips} />} />
                    <Row label='מתוכם סה"כ טיפים' value={<Money value={drawer.tipsTotal} />} />
                  </React.Fragment>
                ))}
                {r.openingCash !== 0 && <Row label="מזומן פתיחה" value={<Money value={r.openingCash} />} bold={rowBold('row:openingCash', true)} />}
                <Row
                  label='סה"כ מזומן בהפחתת טיפים אשראי ומזומן'
                  value={<Money value={roundMoney(r.paymentsCash - r.tipsCredit)} />}
                  bold={rowBold('row:cashMinusCreditTips', true)}
                />
                {r.openingCash !== 0 && (
                  <Row
                    label="מזומן צפוי בקופה"
                    value={<Money value={roundMoney(r.openingCash + drawerCashWithTips)} />}
                    bold={rowBold('row:expectedCash', true)}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}

      <div className={classes.footer}>
        <div className={classes.footerStars}>* * * * *</div>
        <div>תודה לך</div>
      </div>

      <div className={classes.poweredBy}>Powered by CommunicateAI</div>
    </div>
  );
};
